import * as React from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import './AutoExpandScrollTextarea.css';

/** Не ниже ~1 строки: при первом layout в скрытом контейнере / до шрифтов line-height и scrollHeight бывают 0. */
const MIN_TEXTAREA_VIEWPORT_PX = 40;

function getLineHeightPx(el: HTMLElement): number {
  const style = getComputedStyle(el);
  const lineHeight = parseFloat(style.lineHeight);
  if (!Number.isNaN(lineHeight) && lineHeight > 1) return lineHeight;
  const fontSize = parseFloat(style.fontSize) || 16;
  return fontSize * 1.25;
}

function getViewportMaxScrollTop(viewportEl: HTMLElement): number {
  return Math.max(0, viewportEl.scrollHeight - viewportEl.clientHeight);
}

/** Держит строку с кареткой в видимой области viewport (оценка по числу строк). */
function scrollCaretIntoViewport(
  el: HTMLTextAreaElement,
  viewportEl: HTMLElement,
  lineH: number,
  padTop: number
): void {
  const caretIndex = el.selectionStart ?? el.value.length;
  const lineIndex = el.value.slice(0, caretIndex).split('\n').length - 1;
  const caretTop = padTop + lineIndex * lineH;
  const caretBottom = caretTop + lineH;
  const viewTop = viewportEl.scrollTop;
  const viewBottom = viewTop + viewportEl.clientHeight;
  const margin = 4;
  const maxTop = getViewportMaxScrollTop(viewportEl);

  if (caretTop < viewTop + margin) {
    viewportEl.scrollTop = Math.max(0, Math.min(caretTop - margin, maxTop));
  } else if (caretBottom > viewBottom - margin) {
    viewportEl.scrollTop = Math.max(0, Math.min(caretBottom - viewportEl.clientHeight + margin, maxTop));
  }
}

export interface AutoExpandScrollTextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {
  label?: string;
  error?: string;
  fullWidth?: boolean;
  className?: string;
  inputClassName?: string;
  /** После этого числа строк включается вертикальная прокрутка (оверлейный скроллбар) */
  maxVisibleLines?: number;
  characterCount?: { current: number; max: number };
}

/**
 * Многострочное поле: растёт по содержимому до maxVisibleLines строк, далее — прокрутка через OverlayScrollArea.
 */
export const AutoExpandScrollTextarea = React.forwardRef<HTMLTextAreaElement, AutoExpandScrollTextareaProps>(
function AutoExpandScrollTextarea({
  label,
  error,
  fullWidth,
  className = '',
  inputClassName = '',
  id,
  maxVisibleLines = 3,
  value,
  onChange,
  characterCount,
  maxLength,
  disabled,
  onBeforeInput: onBeforeInputProp,
  ...props
}: AutoExpandScrollTextareaProps, forwardedRef) {
  const autoId = React.useId();
  const inputId = id ?? `auto-expand-ta-${autoId}`;
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const viewportRef = useRef<HTMLElement | null>(null);
  const followRafRef = useRef<number | null>(null);
  /** Снимок до setState: в тяжёлых родителях (агент-чат) controlled textarea теряет каретку/scroll. */
  const editSnapshotRef = useRef<{
    scrollTop: number;
    selectionStart: number;
    selectionEnd: number;
  } | null>(null);
  const [viewportMaxPx, setViewportMaxPx] = useState(72);

  const captureEditSnapshot = useCallback((target: HTMLTextAreaElement) => {
    editSnapshotRef.current = {
      scrollTop: viewportRef.current?.scrollTop ?? 0,
      selectionStart: target.selectionStart ?? target.value.length,
      selectionEnd: target.selectionEnd ?? target.value.length,
    };
  }, []);
  const resolvedRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      taRef.current = node;
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else if (forwardedRef) {
        (forwardedRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
      }
    },
    [forwardedRef]
  );

  useEffect(() => {
    return () => {
      if (followRafRef.current != null) {
        cancelAnimationFrame(followRafRef.current);
      }
    };
  }, []);

  useLayoutEffect(() => {
    const el = taRef.current;
    if (!el) return;
    const viewportEl = viewportRef.current;
    const editSnapshot = editSnapshotRef.current;
    editSnapshotRef.current = null;
    const scrollTopBeforeMeasure = editSnapshot?.scrollTop ?? viewportEl?.scrollTop ?? 0;
    const prevMaxTop = viewportEl ? getViewportMaxScrollTop(viewportEl) : 0;
    const wasNearBottom = viewportEl ? prevMaxTop - scrollTopBeforeMeasure <= 2 : false;
    const cs = getComputedStyle(el);
    const fontSize = parseFloat(cs.fontSize) || 16;
    const rawLineH = getLineHeightPx(el);
    const lineH =
      Number.isFinite(rawLineH) && rawLineH > 2 ? rawLineH : Math.max(fontSize * 1.25, MIN_TEXTAREA_VIEWPORT_PX * 0.35);
    const padTop = parseFloat(cs.paddingTop) || 0;
    const padY = padTop + (parseFloat(cs.paddingBottom) || 0);
    const minOneLineH = padY + lineH;
    const maxViewportH = padY + maxVisibleLines * lineH;
    setViewportMaxPx(Math.max(maxViewportH, minOneLineH, MIN_TEXTAREA_VIEWPORT_PX));
    el.style.height = '0px';
    const measuredH = el.scrollHeight;
    el.style.height = `${Math.max(measuredH, minOneLineH, MIN_TEXTAREA_VIEWPORT_PX)}px`;

    if (!viewportEl) return;
    if (followRafRef.current != null) {
      cancelAnimationFrame(followRafRef.current);
      followRafRef.current = null;
    }
    if (el.value.length === 0) {
      viewportEl.scrollTop = 0;
      return;
    }

    const textLength = el.value.length;
    if (editSnapshot) {
      const start = Math.min(editSnapshot.selectionStart, textLength);
      const end = Math.min(editSnapshot.selectionEnd, textLength);
      if (el.selectionStart !== start || el.selectionEnd !== end) {
        try {
          el.setSelectionRange(start, end);
        } catch {
          /* readOnly/disabled */
        }
      }
    }

    const selectionStart = el.selectionStart ?? textLength;
    const selectionEnd = el.selectionEnd ?? textLength;
    const editingAwayFromTail = editSnapshot != null && editSnapshot.selectionStart < textLength;
    const caretAtTail = selectionStart === textLength && selectionEnd === textLength;
    const textareaFocused = typeof document !== 'undefined' && document.activeElement === el;
    const shouldKeepBottomVisible = wasNearBottom || (textareaFocused && caretAtTail);

    const applyViewportScroll = () => {
      if (shouldKeepBottomVisible) {
        viewportEl.scrollTop = getViewportMaxScrollTop(viewportEl);
        return;
      }
      const maxTop = getViewportMaxScrollTop(viewportEl);
      viewportEl.scrollTop = Math.min(scrollTopBeforeMeasure, maxTop);
      if (textareaFocused && !editingAwayFromTail) {
        scrollCaretIntoViewport(el, viewportEl, lineH, padTop);
      }
    };

    applyViewportScroll();
    if (shouldKeepBottomVisible) {
      followRafRef.current = requestAnimationFrame(() => {
        followRafRef.current = null;
        viewportEl.scrollTop = getViewportMaxScrollTop(viewportEl);
      });
    }
  }, [value, maxVisibleLines, disabled]);

  const handleBeforeInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    captureEditSnapshot(e.currentTarget);
    onBeforeInputProp?.(e as React.InputEvent<HTMLTextAreaElement>);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    captureEditSnapshot(e.target);
    let next = e.target.value;
    if (maxLength != null && next.length > maxLength) {
      next = next.slice(0, maxLength);
    }
    onChange?.({
      ...e,
      target: { ...e.target, value: next },
      currentTarget: { ...e.currentTarget, value: next },
    } as React.ChangeEvent<HTMLTextAreaElement>);
  };

  const counterClass = characterCount ? 'form__input_textarea_has-counter' : '';
  const taClass = `form__input form__input_textarea ${counterClass} ${error ? 'form__input_error' : ''} ${inputClassName}`.trim();

  const fieldInner = (
    <OverlayScrollArea
      className={`auto-expand-scroll-textarea__scroll${error ? ' auto-expand-scroll-textarea__scroll_error' : ''}`}
      style={{ maxHeight: viewportMaxPx }}
      viewportRef={viewportRef}
      defer={false}
      osOptions={{ overflow: { x: 'hidden', y: 'scroll' } }}
    >
      <textarea
        ref={resolvedRef}
        id={inputId}
        className={taClass}
        value={value}
        onBeforeInput={handleBeforeInput}
        onChange={handleChange}
        disabled={disabled}
        aria-invalid={!!error}
        aria-describedby={error ? `${inputId}-error` : undefined}
        maxLength={maxLength}
        rows={1}
        {...props}
      />
    </OverlayScrollArea>
  );

  return (
    <div className={`form__field ${fullWidth ? 'form__field_full' : ''} ${className}`.trim()}>
      {label && (
        <label htmlFor={inputId} className="form__label">
          {label}
        </label>
      )}
      {characterCount ? (
        <div className="form__textarea-with-counter auto-expand-scroll-textarea__counter-wrap">
          {fieldInner}
          <span className="form__textarea-counter" aria-live="polite">
            {characterCount.current}/{characterCount.max}
          </span>
        </div>
      ) : (
        fieldInner
      )}
      {error && (
        <span id={`${inputId}-error`} className="form__error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
});
